import { useMemo, useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { PRODUCTS } from "@/data/products";
import { PRODUCT_SPECS, CATEGORY_DEFAULTS } from "@/data/product_specs";
import { useStudio } from "@/context/StudioContext";
import { useSEO, breadcrumb, faqPage, SITE } from "@/lib/useSEO";
import { trackProductViewed } from "@/lib/analytics";
import { productFaqs } from "@/data/product_faqs";

/**
 * ProductDetail — /collection/:productId
 * A rich, SEO-first product page. All hooks run unconditionally (rules of
 * hooks); the not-found redirect happens after every hook has been called.
 */
export default function ProductDetail() {
  const { productId } = useParams();
  const { openSample } = useStudio();
  const [activeColour, setActiveColour] = useState(0);

  const product = useMemo(
    () => PRODUCTS.find((p) => p.id === productId),
    [productId]
  );

  // Fire product_viewed once per product view.
  useEffect(() => {
    if (product) trackProductViewed(product);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const specs = (product && PRODUCT_SPECS[product.id]) || {};
  const cat = (product && CATEGORY_DEFAULTS[product.discipline]) || {};

  const coreSpecs = specs.coreSpecs || cat.coreSpecs || [];
  const care = specs.care || cat.care || [];
  const provenance = specs.provenance || cat.provenance || "";
  const bestUsed = specs.bestUsed || cat.bestUsed || [];
  const warranty = specs.warranty || cat.warranty || "";
  const sustainability = specs.sustainability || cat.sustainability || "";
  const priceBand = specs.priceBand || cat.priceBand || "$$$";

  const related = useMemo(
    () =>
      product
        ? PRODUCTS.filter(
            (p) => p.discipline === product.discipline && p.id !== product.id
          ).slice(0, 3)
        : [],
    [product]
  );

  const currentColour = product?.colours?.[activeColour] || product?.colours?.[0];
  const heroImage = currentColour?.image || product?.swatch;

  const canonicalPath = product ? `/collection/${product.id}` : "/collection";
  const productUrl = `${SITE}${canonicalPath}`;

  // ---- JSON-LD schemas ----
  // NOTE (2026-08-15): this page used to emit `Product` markup. Google Search
  // Console flagged all 25 PDPs (+ the listing items) with "Either 'offers',
  // 'review' or 'aggregateRating' should be specified" — a Product is only
  // valid for product snippets when it carries a real price, and the studio
  // quotes per project rather than publishing prices. So the page is now
  // described as an ItemPage about a Thing: same semantics for crawlers and
  // AI answer engines, no invalid product snippet.
  const pageSchema = product && {
    "@context": "https://schema.org",
    "@type": "ItemPage",
    "@id": `${productUrl}#page`,
    url: productUrl,
    name: `${product.name} · ${product.discipline}`,
    description:
      provenance ||
      `${product.finish} — ${product.discipline} finish curated by My Floor Studio, Sydney.`,
    primaryImageOfPage: {
      "@type": "ImageObject",
      contentUrl: `${SITE}${heroImage}`,
      caption: `${product.name} — ${product.finish}`,
    },
    isPartOf: { "@type": "WebSite", name: "My Floor Studio", url: `${SITE}/` },
    about: {
      "@type": "Thing",
      name: product.name,
      description: `${product.finish} — ${product.discipline}.`,
      image: (product.colours || []).map((c) => `${SITE}${c.image}`),
      additionalProperty: [
        { "@type": "PropertyValue", name: "Element", value: product.discipline },
        { "@type": "PropertyValue", name: "Finish", value: product.finish },
        { "@type": "PropertyValue", name: "Price band", value: priceBand },
        ...(coreSpecs || []).slice(0, 8).map((s) => ({
          "@type": "PropertyValue",
          name: s.label,
          value: s.value,
        })),
      ],
    },
    provider: { "@type": "LocalBusiness", "@id": `${SITE}/#studio`, name: "My Floor Studio" },
  };

  const breadcrumbSchema = breadcrumb([
    { name: "Home", path: "/" },
    { name: "Collection", path: "/collection" },
    { name: product ? product.name : "Product", path: canonicalPath },
  ]);

  const faqSchema = product
    ? faqPage([
        ...productFaqs(product),
        {
          q: `Where is ${product.name} sourced from?`,
          a: provenance,
        },
        {
          q: `Which rooms is ${product.name} best used in?`,
          a: `${product.name} is best used in ${bestUsed.slice(0, 4).join(", ")}. See the full specification below for slip ratings and application notes.`,
        },
        {
          q: `How do I care for ${product.name}?`,
          a: care.slice(0, 2).join(" "),
        },
        {
          q: `Can I order a sample of ${product.name}?`,
          a: `Yes — request a complimentary sample kit from the studio. Sydney orders typically arrive within 2–3 business days.`,
        },
      ])
    : null;

  useSEO({
    title: product
      ? `${product.name} · ${product.discipline} — My Floor Studio`
      : "Collection — My Floor Studio",
    description: product
      ? `${product.name} — ${product.finish}. ${provenance.slice(0, 100)}${provenance.length > 100 ? "…" : ""} Order a sample from the Drummoyne studio.`
      : undefined,
    path: canonicalPath,
    keywords: product
      ? `${product.name}, ${product.discipline.toLowerCase()} flooring, ${product.discipline.toLowerCase()} Sydney, luxury flooring Drummoyne, ${(product.moods || []).join(", ").toLowerCase()}`
      : undefined,
    image: heroImage ? `${SITE}${heroImage}` : undefined,
    imageAlt: product ? `${product.name} — ${product.finish}` : undefined,
    type: "product",
    jsonLd: [pageSchema, breadcrumbSchema, faqSchema].filter(Boolean),
  });

  if (!product) return <Navigate to="/collection" replace />;

  return (
    <div className="min-h-screen bg-[#121315] grain">
      <Nav />
      <main role="main" className="pt-24 md:pt-28">
        {/* Breadcrumb strip */}
        <nav
          aria-label="Breadcrumb"
          className="px-6 md:px-12 py-4 text-[11px] tracking-[0.2em] uppercase text-[#D6D1CA]/55"
        >
          <Link to="/" className="hover:text-[#C9A876]">Home</Link>
          <span className="mx-3">/</span>
          <Link to="/collection" className="hover:text-[#C9A876]">Collection</Link>
          <span className="mx-3">/</span>
          <span className="text-[#F5F4F0]/85">{product.name}</span>
        </nav>

        {/* Hero — image + intro */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-12 px-6 md:px-12 pb-12 md:pb-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="aspect-[4/5] md:aspect-[3/4] w-full overflow-hidden rounded-sm bg-[#0f1012]"
          >
            <img
              src={heroImage}
              alt={`${product.name} — ${currentColour?.name || "primary swatch"}`}
              width={1200}
              height={1500}
              className="w-full h-full object-cover"
              fetchPriority="high"
            />
          </motion.div>

          <div className="pt-8 md:pt-4 md:pl-8">
            <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase mb-4">
              {product.discipline} · {product.tag}
            </p>
            <h1 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-5xl md:text-6xl leading-[0.98]">
              {product.name}
            </h1>
            <p className="mt-6 text-[#D6D1CA]/80 text-lg font-body leading-relaxed">
              {product.finish}
            </p>

            {provenance && (
              <p className="mt-6 text-[#D6D1CA]/70 text-[15px] font-body leading-[1.7]">
                {provenance}
              </p>
            )}

            {/* Colour swatch strip */}
            {product.colours?.length > 1 && (
              <div className="mt-10">
                <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mb-3">
                  {product.colours.length} colours
                </p>
                <div className="flex flex-wrap gap-2">
                  {product.colours.map((c, i) => (
                    <button
                      key={c.name}
                      type="button"
                      onClick={() => setActiveColour(i)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-full border text-[11px] tracking-[0.15em] uppercase transition-colors ${
                        i === activeColour
                          ? "border-[#C9A876] text-[#F5F4F0]"
                          : "border-[#8C7355]/40 text-[#D6D1CA]/70 hover:border-[#C9A876]/70"
                      }`}
                      aria-label={`Show colour ${c.name}`}
                    >
                      <span
                        aria-hidden
                        className="w-3 h-3 rounded-full border border-white/20"
                        style={{ background: c.hex }}
                      />
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Primary CTAs */}
            <div className="mt-10 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={openSample}
                className="inline-block rounded-full bg-[#C9A876] px-7 py-3 text-[11px] tracking-[0.25em] uppercase text-[#121315] hover:bg-[#dcb884] transition-colors"
              >
                Order a sample
              </button>
              <Link
                to="/visualise"
                className="inline-block rounded-full border border-[#8C7355]/50 px-7 py-3 text-[11px] tracking-[0.25em] uppercase text-[#F5F4F0]/90 hover:border-[#C9A876] transition-colors"
              >
                See in a room
              </Link>
            </div>

            <p className="mt-6 text-[11px] tracking-[0.2em] uppercase text-[#D6D1CA]/45">
              Indicative pricing band {priceBand} · formal quote on request
            </p>
          </div>
        </section>

        {/* Specifications */}
        {coreSpecs.length > 0 && (
          <section className="px-6 md:px-12 py-16 md:py-24 border-t border-[#8C7355]/15">
            <div className="max-w-4xl">
              <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase mb-4">
                Technical specification
              </p>
              <h2 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-3xl md:text-4xl leading-tight mb-10">
                What sits beneath the finish.
              </h2>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-4">
                {coreSpecs.map((s) => (
                  <div
                    key={s.label}
                    className="flex justify-between gap-6 py-3 border-b border-[#8C7355]/15 text-[14px] font-body"
                  >
                    <dt className="text-[#D6D1CA]/60 uppercase text-[11px] tracking-[0.2em]">
                      {s.label}
                    </dt>
                    <dd className="text-[#F5F4F0]/90 text-right">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>
        )}

        {/* Care */}
        {care.length > 0 && (
          <section className="px-6 md:px-12 py-16 md:py-24 border-t border-[#8C7355]/15">
            <div className="max-w-3xl">
              <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase mb-4">
                Care & maintenance
              </p>
              <h2 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-3xl md:text-4xl leading-tight mb-10">
                How this floor ages well.
              </h2>
              <ol className="space-y-4">
                {care.map((step, i) => (
                  <li key={i} className="flex gap-6 text-[15px] font-body text-[#D6D1CA]/85 leading-[1.7]">
                    <span className="shrink-0 w-7 text-[11px] tracking-[0.2em] text-[#C9A876]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        )}

        {/* Applications */}
        {bestUsed.length > 0 && (
          <section className="px-6 md:px-12 py-16 md:py-24 border-t border-[#8C7355]/15">
            <div className="max-w-4xl">
              <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase mb-4">
                Where it works best
              </p>
              <h2 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-3xl md:text-4xl leading-tight mb-10">
                Application by room.
              </h2>
              <ul className="flex flex-wrap gap-3">
                {bestUsed.map((room) => (
                  <li
                    key={room}
                    className="rounded-full border border-[#8C7355]/40 px-5 py-2 text-[12px] tracking-[0.15em] uppercase text-[#F5F4F0]/85"
                  >
                    {room}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Warranty + Sustainability */}
        {(warranty || sustainability) && (
          <section className="px-6 md:px-12 py-16 md:py-24 border-t border-[#8C7355]/15 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
            {warranty && (
              <div>
                <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase mb-3">
                  Warranty
                </p>
                <p className="text-[#D6D1CA]/85 text-lg font-body leading-[1.6]">
                  {warranty}
                </p>
              </div>
            )}
            {sustainability && (
              <div>
                <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase mb-3">
                  Sustainability
                </p>
                <p className="text-[#D6D1CA]/85 text-lg font-body leading-[1.6]">
                  {sustainability}
                </p>
              </div>
            )}
          </section>
        )}

        {/* Related */}
        {related.length > 0 && (
          <section className="px-6 md:px-12 py-16 md:py-24 border-t border-[#8C7355]/15">
            <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase mb-4">
              From the same element
            </p>
            <h2 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-3xl md:text-4xl leading-tight mb-10">
              Related {product.discipline.toLowerCase()}.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/collection/${r.id}`}
                  className="group block"
                >
                  <div className="aspect-[4/5] overflow-hidden rounded-sm bg-[#0f1012]">
                    <img
                      src={r.swatch}
                      alt={r.name}
                      width={800}
                      height={1000}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    />
                  </div>
                  <p className="mt-4 text-[11px] tracking-[0.4em] uppercase text-[#C9A876]">
                    {r.discipline}
                  </p>
                  <h3 className="font-serif-display italic text-[#F5F4F0] text-2xl mt-1">
                    {r.name}
                  </h3>
                  <p className="text-[#D6D1CA]/60 text-sm mt-1 font-body">{r.finish}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
